import { Credential } from '@/model/Authentication';
import HttpEndpoint from '@/model/enums/http/http-endpoint';
import HttpHeader from '@/model/enums/http/http-header';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import store from '@/store';
import router from '@/router';
import { ApiError } from '@/model/ApiError';
import HttpStatus from '@/model/enums/http/http-status';

const ApiClient = axios.create();
const ApiClientWithResponse = axios.create();

ApiClient.defaults.baseURL = process.env.VUE_APP_BASE_URL;
ApiClientWithResponse.defaults.baseURL = process.env.VUE_APP_BASE_URL;

function applyRequestInterceptor(client: AxiosInstance): void {
    client.interceptors.request.use(async (config) => {
        const credential: Credential =
            store.getters['authenticationStorage/getCredential'];

        if (config?.headers) {
            if (
                credential?.accessToken &&
                Date.now() >= credential.accessTokenExpiration &&
                HttpEndpoint.REFRESH_URL !== config.url
            ) {
                if (Date.now() >= credential.refreshTokenExpiration) {
                    store.commit('authenticationStorage/setCredential', null);
                    router.push({ name: 'Login' });
                    return config;
                }

                // eslint-disable-next-line no-param-reassign
                config.headers[HttpHeader.AUTHORIZATION] = '';

                await store.dispatch('authenticationStorage/refresh');
            }

            const refreshedCredential: Credential =
                store.getters['authenticationStorage/getCredential'];

            if (
                refreshedCredential?.accessToken &&
                HttpEndpoint.REFRESH_URL !== config.url
            ) {
                // eslint-disable-next-line no-param-reassign
                config.headers[
                    HttpHeader.AUTHORIZATION
                ] = `Bearer ${refreshedCredential.accessToken}`;
            }
        }

        return config;
    });
}

async function mapApiError(error: AxiosError): Promise<ApiError> {
    const apiError: ApiError = { key: 'general.internal.server.error' };
    const responseData = error?.response?.data;

    if (responseData instanceof Blob) {
        const contentType = String(
            error.response?.headers?.['content-type'] ?? '',
        );

        if (contentType.includes('application/json')) {
            try {
                const parsedResponse = JSON.parse(await responseData.text());

                if (parsedResponse?.key) {
                    apiError.key = parsedResponse.key;
                }

                if (parsedResponse?.validationMessages) {
                    apiError.validationMessages =
                        parsedResponse.validationMessages;
                }
            } catch {
                return apiError;
            }
        }

        return apiError;
    }

    if ((responseData as ApiError | undefined)?.key) {
        apiError.key = (responseData as ApiError).key;
    }

    if ((responseData as ApiError | undefined)?.validationMessages) {
        apiError.validationMessages = (
            responseData as ApiError
        ).validationMessages;
    }

    return apiError;
}

function applyResponseInterceptor(
    client: AxiosInstance,
    returnResponse = false,
): void {
    client.interceptors.response.use(
        (response: AxiosResponse) => {
            return Promise.resolve(returnResponse ? response : response?.data);
        },
        async (error: AxiosError) => {
            if (error?.response?.status === HttpStatus.TOKEN_EXPIRED) {
                store.commit('authenticationStorage/setCredential', null);
                router.push({ name: 'Login' });
                return Promise.reject(error);
            }

            return Promise.reject(await mapApiError(error));
        },
    );
}

applyRequestInterceptor(ApiClient);
applyRequestInterceptor(ApiClientWithResponse);
applyResponseInterceptor(ApiClient);
applyResponseInterceptor(ApiClientWithResponse, true);

export default ApiClient;
export { ApiClientWithResponse };
